import { TextLinkWidget } from '@tableau/widgets';
import * as React from 'react';

import { getAppStore } from '~src/app/AppStore';
import { CompositeSessionInfoSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { buttonSize, FlowTaskInfoItem, itemClass } from '~src/flow/components/FlowTaskInfoItem';
import { headerStyle, scheduleIcon, scheduleIconWrapper } from '~src/flow/components/FlowTaskOutputsDialog';
import { FlowTaskOutputsGrid } from '~src/flow/components/FlowTaskOutputsGrid';
import { classes, cssClass } from '~src/style';
import { LinkedTaskInfo } from '~src/ts/models/LinkedTaskInfo';
import { LinkedTaskItemInfo } from '~src/ts/models/LinkedTaskItemInfo';
import { errorActionRed } from '~src/userNotifications/styles/notificationStyles';

import { LinkedTasks } from '../resources/LinkedTasks';
import { editLinkedTaskDialog } from './EditLinkedTaskDialog';
import { numberStyle } from './LinkedTaskItem';

const rootClass = 'LinkedTaskDetailsDialog';

const scrollWrapper = cssClass(rootClass, 'scrollWrapper', {
  height: 'calc(100% - 36px)',
  overflow: 'auto',
});

const taskListWrapper = cssClass(rootClass, 'taskListWrapper', {
  marginTop: 4,
  $nest: {
    '& > div:not(:first-child)': {
      marginTop: 16,
    },
  },
});

const taskWrapperStyle = cssClass(rootClass, 'wrapperStyle', {
  display: 'flex',
  alignItems: 'baseline',
});

const flowErrorStyle = cssClass(rootClass, 'flowErrorStyle', {
  width: '100%',
  height: buttonSize,
  color: errorActionRed,
});

const errorIconStyle = cssClass(rootClass, 'errorIconStyle', {
  marginRight: 6,
});

function onAction(props: LinkedTaskDetailsDialogProps) {
  editLinkedTaskDialog(props.linkedTask);
  return Promise.resolve();
}

export function getAndOpenLinkedTaskDetailsDialog(linkedTaskId: string) {
  return LinkedTasks.getLinkedTask(linkedTaskId).then(response => {
    return openLinkedTaskDetailsDialog({ linkedTask: response });
  });
}

export function openLinkedTaskDetailsDialog(props: LinkedTaskDetailsDialogProps) {
  const modalInstance = openReactActionDialog({
    title: Messages.linkedTasks(),
    bodyComponent: LinkedTaskDetailsDialogContent,
    initialBodyProps: {
      ...props,
    },
    initialDialogState: {},
    actionName: Messages.linkedTasksEditTasks(),
    action: onAction,
    dialogWidth: 800,
    dialogHeight: 450,
    dismissDialogOnActionFailed: false,
    showCloseButton: true,
    cancelButtonText: Messages.close(),
  });

  return modalInstance.result;
}

export interface LinkedTaskDetailsDialogProps {
  linkedTask: LinkedTaskInfo;
}

export type LinkedTaskDetailsActionDialogProps = ReactActionDialogBodyProps<LinkedTaskDetailsDialogProps, {}>;

export function LinkedTaskDetailsDialogContent(props: LinkedTaskDetailsActionDialogProps): JSX.Element {
  function renderScheduleText(): JSX.Element {
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    const isUserSiteOrServerAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState());
    return isUserSiteOrServerAdmin ?
      <TextLinkWidget
        href={siteUrls.scheduleFlowsUrl(props.linkedTask.schedule.id)}
        title={props.linkedTask.schedule.name}
        testId={`linked-tast-detail-dialog-link-${props.linkedTask.schedule.id}`}
      >
        {props.linkedTask.schedule.name}
      </TextLinkWidget>
      :
      <div title={props.linkedTask.schedule.name}>
        {props.linkedTask.schedule.name}
      </div>;
  }

  function renderEmptyTask(index: number): JSX.Element {
    return (
      <div className={taskWrapperStyle}>
        <div className={numberStyle}>{index + 1}</div>
        <div className={classes(itemClass, flowErrorStyle)}>
          <img src={imgPath('common_alert_error.svg')} alt={''} className={errorIconStyle} />
          {Messages.linkedTasksFlowNotFoundError()}
        </div>
      </div>
    );
  }

  function renderTasks(): JSX.Element[] {
    return props.linkedTask.linkedTaskItems.map((linkedTaskItem: LinkedTaskItemInfo, index: number) => {
      if (linkedTaskItem === null) {
        return renderEmptyTask(index);
      }
      return (
        <div key={`${linkedTaskItem.id}-${index}`} className={taskWrapperStyle}>
          <div className={numberStyle}>{linkedTaskItem.stepNumber}</div>
          <FlowTaskInfoItem
            taskId={linkedTaskItem.id}
            flowName={linkedTaskItem.name}
            flowId={linkedTaskItem.targetId}
            lastRun={linkedTaskItem.lastRunAt ? new Date(linkedTaskItem.lastRunAt) : null}
            taskStatus={linkedTaskItem.status}
            shouldObfuscate={linkedTaskItem.shouldObfuscate}
            expansionContent={linkedTaskItem.shouldObfuscate ? null
              : <FlowTaskOutputsGrid outputIds={linkedTaskItem.flowOutputStepIds} outputSteps={linkedTaskItem.flowOutputSteps} />}
          />
        </div>
      );
    });
  }

  return (
    <div className={scrollWrapper}>
      <div className={headerStyle}>
        <div className={scheduleIconWrapper}>
          <img src={imgPath('schedule_content_type_small.svg')} className={scheduleIcon} alt={''} />
          {Messages.schedule()}
        </div>
        <div>{renderScheduleText()}</div>
      </div>
      <div className={taskListWrapper}>
        {renderTasks()}
      </div>
    </div>
  );
}
